<script lang="ts">
  import { formatTime } from '$lib/format';
  import { t } from '$lib/i18n/index.svelte';
  import type { ConversationItem } from '$lib/db/threads';

  import type { ElementContext } from '../elements/context';
  import ElementView from '../elements/ElementView.svelte';

  let {
    item,
    ctx,
    onretry,
    ondiscard,
  }: {
    item: ConversationItem;
    ctx: ElementContext;
    onretry: (uuid: string) => void;
    ondiscard: (uuid: string) => void;
  } = $props();

  // The author line renders kind and name TOGETHER, never the name alone — a
  // rule from common.yaml: "Anna" from an agent and "Anna" the human must not
  // look alike.
  const authorLabel = $derived.by(() => {
    if (item.fromUser) {
      return undefined;
    }
    const kindLabel =
      item.authorKind === 'agent'
        ? t('author.agent')
        : item.authorKind === 'human'
          ? t('author.human')
          : t('author.system');
    return item.authorName !== undefined ? `${item.authorName} · ${kindLabel}` : kindLabel;
  });
</script>

<div class="row" class:mine={item.fromUser}>
  <div class="bubble" class:pending={item.sendState === 'queued'} class:failed={item.sendState === 'error'}>
    {#if authorLabel !== undefined}
      <div class="author">{authorLabel}</div>
    {/if}
    {#each item.elements as element, i (i)}
      <div class="element">
        <ElementView {element} ctx={item.sendState === undefined ? ctx : { ...ctx, disabled: true }} />
      </div>
    {/each}
    <div class="foot">
      {#if item.sendState === 'queued'}
        <span class="state">{t('conv.pending')}</span>
      {:else if item.sendState === 'error'}
        <span class="state error">{t('conv.failed')}{item.sendError !== undefined ? ` (${item.sendError})` : ''}</span>
        <button onclick={() => onretry(item.id)}>{t('conv.retry')}</button>
        <button class="discard" onclick={() => ondiscard(item.id)}>{t('conv.discard')}</button>
      {:else}
        <span class="time">{formatTime(item.timeMs)}</span>
      {/if}
    </div>
  </div>
</div>

<style>
  .row {
    display: flex;
    padding: 2px 12px;
  }
  .row.mine {
    justify-content: flex-end;
  }
  .bubble {
    max-width: 86%;
    background: var(--bubble-theirs);
    border: 1px solid var(--line);
    border-radius: var(--radius);
    padding: 10px 12px;
  }
  .mine .bubble {
    background: var(--bubble-mine);
    border-color: transparent;
  }
  .pending {
    opacity: 0.7;
  }
  .failed {
    border-color: var(--danger);
  }
  .author {
    font-size: 12px;
    font-weight: 600;
    color: var(--brand);
    margin-bottom: 4px;
  }
  .element + .element {
    margin-top: 8px;
  }
  .foot {
    display: flex;
    justify-content: flex-end;
    align-items: center;
    gap: 10px;
    margin-top: 4px;
  }
  .time,
  .state {
    font-size: 11px;
    color: var(--ink-soft);
  }
  .state.error {
    color: var(--danger);
  }
  .foot button {
    border: 0;
    background: transparent;
    color: var(--brand);
    font-size: 12px;
    font-weight: 600;
    cursor: pointer;
    padding: 0;
  }
  .foot .discard {
    color: var(--ink-soft);
  }
</style>
